/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/cm-observations.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useQuery } from '@apollo/client/react';
import React from 'react';
import Constants from '../../../../../utils/constants';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { useLocalState } from '@hooks/index';
import { getPanelName } from '@utils/cm-observations.service';
import { GET_DATASETS, GET_PANELS_FOR_OBSERVATION } from '@queries/central-monitoring';
import {
    dataSets,
    getPanelsForObservation,
    getPanelsForObservationVariables,
} from '@queries/generated/central-monitoring.generated';
import { GET_SITES } from '@queries/sites';
import { SiteFields } from '@queries/generated/fragments.generated';
import { getSites, getSitesVariables } from '@queries/generated/sites.generated';

const propArray = ['Description', 'QtlExceeded', 'Type', 'ClosedDatePopulated'];

const CMObservationRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const { state } = useLocalState();

    const { data: dataSetsData } = useQuery<dataSets>(GET_DATASETS, {
        variables: {
            studyId: newItem?.studyId ?? '',
        },
    });

    const obsDataset = dataSetsData?.dataSets?.find((d) => d?.id === newItem.dataSetId)?.processPanelsDate;

    const { data: dataSites } = useQuery<getSites, getSitesVariables>(GET_SITES, {
        variables: {
            studyId: newItem?.studyId ?? '',
            timestamp: obsDataset ?? '',
        },
        fetchPolicy: 'no-cache',
    });

    const { data: panelsData } = useQuery<getPanelsForObservation, getPanelsForObservationVariables>(
        GET_PANELS_FOR_OBSERVATION,
        {
            variables: {
                organisationId: state?.selectedOrganisation?.id || state?.selectedStudy?.organisation?.id,
                studyId: Constants.ObjectId.Empty,
                timestamp: obsDataset ?? '',
            },
            fetchPolicy: 'no-cache',
        },
    );

    // When switching the panel types between two dummy panels, the only item in the audit log entry that changes is the Type.
    // If the type changes we need to force the PanelId to be rendered in the log.
    // The below will add a PanelId field to the refactored item and the lookups then populates the panel Id with the correct value
    const showPanelNameOnTypeChange = () => {
        if (oldItem?.type && oldItem?.type !== newItem?.type) {
            return true;
        }
        return null;
    };

    const refactoredOldRecord = {
        ...oldRecord,
        Observation: oldRecord.Description,
        Deleted: oldRecord.D,
        PanelId: showPanelNameOnTypeChange(),
        ...removePropertiesFromObject(oldRecord, propArray),
    };
    const refactoredNewRecord = {
        ...newRecord,
        Observation: newRecord.Description,
        Deleted: newRecord.D,
        PanelId: showPanelNameOnTypeChange(),
        ...removePropertiesFromObject(newRecord, propArray),
    };

    const findSiteName = (item: any) => {
        const foundSite = dataSites?.sites?.find((site: SiteFields | null) => site?.id === item?.siteId);
        return foundSite?.name;
    };

    const findPanelName = (item: any) => {
        if (!item) {
            return null;
        }

        const matchingPanel = panelsData?.panels?.find((panel) => panel?.id === item?.panelId);
        return getPanelName(matchingPanel, item.type);
    };

    const lookups: any = {
        SiteId: findSiteName,
        PanelId: findPanelName,
        OwnerId: (observation: any) =>
            observation?.owner ? `${observation?.owner?.firstName} ${observation?.owner?.lastName}` : ' ',
    };
    const associations: any = {
        ActionIds: {
            itemField: 'actions',
            name: 'Action',
            nameResolver: (action: any) => action.description,
        },
    };
    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            lookups,
            associations,
        }),
    );

    return <FieldChangeGrid items={items} />;
};
export { CMObservationRecordChangeGrid };
